import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Package, ShoppingCart, Wrench, Clock, AlertCircle } from 'lucide-react';
import { TimeFilter, PeriodType } from './TimeFilter';
import { StatCard } from './StatCard';
import { PageHeader } from '../common/ui';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';
import { useAuth } from '../../contexts/AuthContext';

interface UserEfficiency {
  user_id: number;
  user_name: string;
  production_seconds: number;
  repair_seconds: number;
  other_seconds: number;
  total_seconds: number;
  devices_created: number;
  repairs_done: number;
  avg_per_device: number;
  avg_per_repair: number;
}

interface OverviewStats {
  period: { start: string; end: string };
  devices: { created: number; status_changed_from_active: number };
  orders: { devices_sold: number; orders_completed: number };
  repairs: { created: number; completed: number; warranty: number; non_warranty: number };
  time?: {
    total_seconds: number;
    entry_count: number;
    active_projects: number;
    production_seconds: number;
    repair_seconds: number;
    other_seconds: number;
    avg_per_device_created: number;
    avg_per_repair: number;
    user_efficiency: UserEfficiency[];
  };
}

function formatHours(seconds: number): string {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const StatsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth() as any;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const hasTimeAccess = user?.time_tracking_enabled || user?.time_master;
  const isTimeMaster = user?.time_master;

  const [period, setPeriod] = useState<PeriodType>(() => {
    const p = searchParams.get('period');
    if (p && ['7d', '30d', '365d', 'this_month', 'this_year', 'custom'].includes(p)) {
      return p as PeriodType;
    }
    const saved = getUserPreference('statsOverview', 'period', '30d');
    if (['7d', '30d', '365d', 'this_month', 'this_year'].includes(saved)) {
      return saved as PeriodType;
    }
    return '30d';
  });
  
  const [customStart, setCustomStart] = useState(() => searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState(() => searchParams.get('end') || '');
  
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      
      let url = `/api/stats/overview?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `/api/stats/overview?start=${customStart}&end=${customEnd}`;
      }
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching overview stats:', err);
      setError(err.response?.data?.error || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, customStart, customEnd]);

  const handlePeriodChange = (newPeriod: PeriodType, start?: string, end?: string) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setUserPreference('statsOverview', 'period', newPeriod);
    }
    
    const params = new URLSearchParams();
    if (newPeriod === 'custom' && start && end) {
      params.set('start', start);
      params.set('end', end);
      setCustomStart(start);
      setCustomEnd(end);
    } else {
      params.set('period', newPeriod);
      setCustomStart('');
      setCustomEnd('');
    }
    setSearchParams(params);
  };

  const buildQueryString = () => {
    if (period === 'custom' && customStart && customEnd) {
      return `?start=${customStart}&end=${customEnd}`;
    }
    return `?period=${period}`;
  };

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow p-6 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
    </div>
  );

  const timeData = stats?.time;
  const userEfficiency = timeData?.user_efficiency || [];
  const totals = userEfficiency.reduce((acc, u) => ({
    production_seconds: acc.production_seconds + u.production_seconds,
    repair_seconds: acc.repair_seconds + u.repair_seconds,
    other_seconds: acc.other_seconds + u.other_seconds,
    total_seconds: acc.total_seconds + u.total_seconds,
    devices_created: acc.devices_created + u.devices_created,
    repairs_done: acc.repairs_done + u.repairs_done
  }), { production_seconds: 0, repair_seconds: 0, other_seconds: 0, total_seconds: 0, devices_created: 0, repairs_done: 0 });

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Statistics"
        subtitle="Overview of devices, orders, and repairs"
        onBack={undefined}
      />

      <TimeFilter
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onChange={handlePeriodChange}
      />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          {hasTimeAccess && <SkeletonCard />}
        </div>
      ) : stats ? (
        <>
          {/* Main Stat Cards */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${hasTimeAccess ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-8`}>
            <StatCard
              title="Devices"
              value={stats.devices.created}
              subtitle="Devices created"
              icon={Package}
              iconColor="bg-blue-500"
              linkTo={`/stats/devices${buildQueryString()}`}
              breakdown={[
                {
                  label: 'Status changed from active',
                  value: stats.devices.status_changed_from_active,
                  color: stats.devices.status_changed_from_active > 0 ? 'text-orange-600' : undefined
                }
              ]}
            />

            <StatCard
              title="Orders"
              value={stats.orders.devices_sold}
              subtitle="Devices sold"
              icon={ShoppingCart}
              iconColor="bg-green-500"
              linkTo={`/stats/orders${buildQueryString()}`}
              breakdown={[
                {
                  label: 'Orders completed',
                  value: stats.orders.orders_completed
                }
              ]}
            />

            <StatCard
              title="Repairs"
              value={stats.repairs.completed}
              subtitle="Repairs completed"
              icon={Wrench}
              iconColor="bg-purple-500"
              linkTo={`/stats/repairs${buildQueryString()}`}
              breakdown={[
                {
                  label: 'Repairs created',
                  value: stats.repairs.created
                },
                {
                  label: 'Warranty repairs',
                  value: stats.repairs.warranty,
                  color: 'text-amber-600'
                },
                {
                  label: 'Non-warranty repairs',
                  value: stats.repairs.non_warranty
                }
              ]}
            />

            {hasTimeAccess && timeData && (
              <StatCard
                title="Time"
                value={formatHours(timeData.total_seconds)}
                subtitle="Total tracked time"
                icon={Clock}
                iconColor="bg-teal-500"
                linkTo={`/stats/time${buildQueryString()}`}
                breakdown={[
                  { label: 'Production', value: formatHours(timeData.production_seconds) },
                  { label: 'Repair', value: formatHours(timeData.repair_seconds) },
                  ...(timeData.other_seconds > 0 ? [{ label: 'Other', value: formatHours(timeData.other_seconds) }] : [])
                ]}
              />
            )}
          </div>

          {/* Period Summary */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Period Summary</h3>
            <div className={`grid grid-cols-2 ${hasTimeAccess ? 'md:grid-cols-3 lg:grid-cols-6' : 'md:grid-cols-4'} gap-6`}>
              <div className="text-center">
                <p className="text-3xl font-bold text-naviter-blue">{stats.devices.created}</p>
                <p className="text-sm text-gray-500 mt-1">Devices Created</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{stats.orders.devices_sold}</p>
                <p className="text-sm text-gray-500 mt-1">Devices Sold</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{stats.repairs.completed}</p>
                <p className="text-sm text-gray-500 mt-1">Repairs Completed</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">
                  {stats.repairs.completed > 0 
                    ? `${Math.round((stats.repairs.warranty / stats.repairs.completed) * 100)}%`
                    : '0%'
                  }
                </p>
                <p className="text-sm text-gray-500 mt-1">Warranty Rate</p>
              </div>
              {hasTimeAccess && timeData && (
                <>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-teal-600">{formatHours(timeData.avg_per_device_created)}</p>
                    <p className="text-sm text-gray-500 mt-1">Avg Time / Device</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-indigo-600">{formatHours(timeData.avg_per_repair)}</p>
                    <p className="text-sm text-gray-500 mt-1">Avg Time / Repair</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Per-user efficiency table (time_master only) */}
          {isTimeMaster && userEfficiency.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Team Efficiency</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase">Production</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase">Devices</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 uppercase">Avg/Device</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-purple-600 uppercase">Repair</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-purple-600 uppercase">Repairs</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-purple-600 uppercase">Avg/Repair</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userEfficiency.map(u => (
                      <tr key={u.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.user_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">{formatHours(u.production_seconds)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">{u.devices_created || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">{u.avg_per_device ? formatHours(u.avg_per_device) : '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">{formatHours(u.repair_seconds)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">{u.repairs_done || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums">{u.avg_per_repair ? formatHours(u.avg_per_repair) : '—'}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right tabular-nums">{formatHours(u.total_seconds)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">{formatHours(totals.production_seconds)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{totals.devices_created || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">
                        {totals.devices_created > 0 ? formatHours(Math.round(totals.production_seconds / totals.devices_created)) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">{formatHours(totals.repair_seconds)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{totals.repairs_done || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">
                        {totals.repairs_done > 0 ? formatHours(Math.round(totals.repair_seconds / totals.repairs_done)) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right tabular-nums">{formatHours(totals.total_seconds)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default StatsOverview;
